import { apiFetch } from "@/lib/apiClient";

/** One hospital a patient can connect to via Epic. `city`/`state` aren't in the
 *  backend's provider_organizations table (only id/name/fhir_base_url/environment
 *  are synced from Epic's directory), so they're intentionally not part of this
 *  shape — do not invent placeholder values for them. */
export type EpicOrganization = {
  id: string;
  name: string;
};

/** GET /api/epic/organizations — the enabled hospitals a patient can pick from. */
export async function listEpicOrganizations(): Promise<EpicOrganization[]> {
  const { data, error } = await apiFetch<EpicOrganization[]>("/api/epic/organizations", {
    method: "GET",
    portal: "patient",
  });

  if (error) {
    throw new Error(error.message || "Failed to load hospitals");
  }

  return data ?? [];
}

/** GET /auth/epic/connect — Bearer-authenticated JSON endpoint (not a raw redirect
 *  target: the patient's session lives in an in-memory token + path-scoped cookie,
 *  neither of which reach a plain browser navigation). Returns the real Epic
 *  authorize URL; the caller performs the actual `window.location.href` navigation. */
export async function getEpicAuthorizeUrl(organizationId: string): Promise<string> {
  const { data, error } = await apiFetch<{ authorize_url: string }>(
    `/auth/epic/connect?organization_id=${encodeURIComponent(organizationId)}`,
    { method: "GET", portal: "patient" },
  );

  if (error) {
    throw new Error(error.message || "Failed to start the Epic connection");
  }
  if (!data?.authorize_url) {
    throw new Error("Epic did not return an authorization URL");
  }

  return data.authorize_url;
}

export type EpicSyncResponse = {
  sync_job_id: string;
};

/** POST /api/patient/epic/sync — enqueues a pull of the connected hospital's
 *  FHIR data onto the same worker file uploads use, returning immediately
 *  with a sync_job_id rather than blocking for the whole fetch. Progress and
 *  results are then watched the same way an upload's are: poll
 *  getSyncJobStatus(sync_job_id) (syncJobApi.ts) for live per-resource-type
 *  progress, and read the stored records via getPatientRecordsDetailed with
 *  uploadId set to this same sync_job_id (FHIR rows this sync writes are
 *  tagged with it — see the backend's epic_integration/service.py). */
export async function syncEpicData(): Promise<EpicSyncResponse> {
  const { data, error } = await apiFetch<EpicSyncResponse>("/api/patient/epic/sync", {
    method: "POST",
    portal: "patient",
  });

  if (error) {
    throw new Error(error.message || "Failed to sync your Epic records");
  }
  if (!data?.sync_job_id) {
    throw new Error("Epic sync did not return a job id");
  }

  return data;
}
